import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import type { QuestionFeedback as Feedback, QuestionsApi } from '../api/questions'

const REASONS: Record<NonNullable<Feedback['reason']>, string> = {
  UnclearWording: 'Unclear wording',
  AnswerSeemsWrong: 'Answer seems wrong',
  SolutionUnclear: 'Solution unclear',
  Typo: 'Typo',
  Other: 'Other',
}

/**
 * Every Student's Q Feedback on one Question (app:R63), newest first, one row each. Who
 * wrote it is not shown: the words and the answer beside them are what an Admin acts on.
 *
 * OPENING IT IS READING IT. The Question's feedback is marked read as the table arrives, so
 * the grid's count and the nav's go down; the rows that were new stay marked New for as long
 * as the table is open, because the list is not fetched again underneath the reader.
 */
export function QuestionFeedback({ api, id }: { api: QuestionsApi; id: number }) {
  const client = useQueryClient()
  const marked = useRef(false)

  const feedback = useQuery({
    queryKey: ['question-feedback', id],
    queryFn: () => api.feedback(id),
  })

  const read = useMutation({
    mutationFn: () => api.markRead(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['questions'] })
      void client.invalidateQueries({ queryKey: ['feedback-unread'] })
    },
  })

  const unread = feedback.data?.some((entry) => !entry.readAt) ?? false
  useEffect(() => {
    if (!unread || marked.current) return
    marked.current = true
    read.mutate()
  }, [unread, read])

  if (feedback.isPending) return <p className="text-muted">Reading the feedback...</p>
  if (feedback.isError) {
    return (
      <p role="alert" className="border-l-2 border-red-700 pl-3 text-red-700">
        {feedback.error.message}
      </p>
    )
  }
  if (feedback.data.length === 0) return <p className="text-muted">No Q Feedback on this Question yet.</p>

  return (
    <>
      {read.isError && (
        <p role="alert" className="mb-3 border-l-2 border-red-700 pl-3 text-red-700">
          {read.error.message}
        </p>
      )}
      <table aria-label={`Q Feedback on Question ${id}`} className="w-full border-collapse text-small">
        <thead>
          <tr className="border-b border-ink-100 text-left text-muted">
            <th scope="col" className="py-2 pr-3 font-bold">Received</th>
            <th scope="col" className="py-2 pr-3 font-bold">Reason</th>
            <th scope="col" className="py-2 pr-3 font-bold">Answer</th>
            <th scope="col" className="py-2 pr-3 font-bold">Feedback</th>
            <th scope="col" className="py-2 font-bold">Status</th>
          </tr>
        </thead>
        <tbody>
          {feedback.data.map((entry) => (
            <tr key={entry.id} className="border-b border-ink-100 align-top">
              <td className="py-2 pr-3 whitespace-nowrap">
                <time dateTime={entry.reportedAt}>{londonTime(entry.reportedAt)}</time>
              </td>
              <td className="py-2 pr-3 whitespace-nowrap">{entry.reason ? REASONS[entry.reason] : '—'}</td>
              <td className="py-2 pr-3 whitespace-nowrap">{answered(entry)}</td>
              <td className="py-2 pr-3 whitespace-pre-wrap text-body">{entry.what}</td>
              <td className="py-2 whitespace-nowrap">
                {entry.readAt ? (
                  entry.state === 'Reported' ? 'Read' : entry.state
                ) : (
                  <span className="font-bold text-green-800">New</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

function answered(entry: Feedback): string {
  if (entry.chose === null || entry.chose === undefined) return 'Not answered'
  if (entry.wasCorrect === null || entry.wasCorrect === undefined) return entry.chose
  return `${entry.chose}, ${entry.wasCorrect ? 'right' : 'wrong'}`
}

/** Everything a person reads here is on the Europe/London clock (platform:R02). */
export function londonTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/London',
  })
}
